import type { ToolCall } from '@diagon-alley/common';
import type { SubmitToolResponseArgs } from '@hooks/useReportingThread';

import ClarificationCard from './ClarificationCard';
import KpiScorecardBlock from './KpiScorecardBlock';
import ProgressCard from './ProgressCard';
import ReportActionBar from './ReportActionBar';
import ReportPreviewCard from './ReportPreviewCard';
import ResultChartCard from './ResultChartCard';
import ResultTableCard from './ResultTableCard';
import SaveReportRow from './SaveReportRow';

/**
 * Generative-UI dispatch: turns a typed ToolCall into the matching
 * Mantine component. The ToolCall payload has been validated against
 * its Zod schema upstream (in useReportingThread) so payload access
 * here is type-safe.
 *
 * Interactive cards (clarification, preview) accept a
 * `onSubmitToolResponse` callback to round-trip selections back to
 * the agent. The runtime toolCallId for these is extracted from the
 * outer message id at the call-site (the orchestrator generates ids
 * like "showClarificationCard-1234567890").
 */
export default function ToolCallRenderer({
    toolCall,
    toolCallId,
    onSubmitToolResponse,
}: {
    toolCall: ToolCall;
    toolCallId: string;
    onSubmitToolResponse?: (args: SubmitToolResponseArgs) => Promise<void>;
}) {
    switch (toolCall.name) {
        case 'showResultTable':
            return <ResultTableCard payload={toolCall.payload} />;
        case 'showResultChart':
            return <ResultChartCard payload={toolCall.payload} />;
        case 'showKpiScorecard':
            return <KpiScorecardBlock payload={toolCall.payload} />;
        case 'showDownloadArtifact':
            return <ReportActionBar payload={toolCall.payload} />;
        case 'showClarificationCard':
            return (
                <ClarificationCard
                    payload={toolCall.payload}
                    toolCallId={toolCallId}
                    onSubmit={onSubmitToolResponse}
                />
            );
        case 'showReportPreview':
            return (
                <ReportPreviewCard
                    payload={toolCall.payload}
                    toolCallId={toolCallId}
                    onSubmit={onSubmitToolResponse}
                />
            );
        case 'showProgress':
            return <ProgressCard payload={toolCall.payload} />;
        case 'offerSaveReport':
            return <SaveReportRow payload={toolCall.payload} />;
        default:
            return null;
    }
}
